/**
 * Front-end money helpers for the backoffice.
 *
 * Money is stored and sent over the wire as integer minor units (satang for
 * THB). The per-currency exponent mirrors CLAUDE.md §2.4 and the backend
 * `@b1fr0st/shared/money` (which the SPA must not import — it pulls in pino /
 * postgres). Parsing and formatting happen here at the HTTP boundary WITHOUT
 * floating-point math: never `Math.round(Number(x) * 100)`, which both
 * violates §2.2 and hardcodes a 2-decimal assumption that corrupts JPY/KRW
 * (0dp) and the Gulf dinars (3dp).
 */

/**
 * Only the NON-default exponents have to be listed — anything absent reads as 2.
 * That makes the 0dp and 3dp rows load-bearing and the 2dp rows decorative, and
 * it is why this table is pinned against `packages/shared/src/money.ts` by
 * `money/shared-exponent-parity.test.ts`: a code missing from THOSE two rows is
 * not "unsupported", it is silently priced 100x or 1000x wrong (PYG, RWF, XAF,
 * XOF, IQD and LYD were).
 *
 * Mirroring rather than throwing on an unknown code is deliberate. `decimalsFor`
 * is reached by `fromMinor`/`moneyHint`/`formatMoney` on every money cell of
 * every table, including ~48 sites that pass no currency at all and rely on the
 * `= 'THB'` default; a throw there blanks a page over one row. The list of codes
 * that may be CHOSEN is enforced server-side by `isSupportedCurrency`, so this
 * table's job is to agree with that list, not to police it.
 */
const EXPONENT: Record<string, number> = {
  THB: 2, USD: 2, EUR: 2, GBP: 2, AUD: 2, CNY: 2, SGD: 2, MYR: 2,
  IDR: 2, PHP: 2, INR: 2,
  JPY: 0, KRW: 0, VND: 0, LAK: 0, CLP: 0, PYG: 0, RWF: 0, XAF: 0, XOF: 0,
  TND: 3, BHD: 3, KWD: 3, OMR: 3, JOD: 3, IQD: 3, LYD: 3,
};

const SYMBOLS: Record<string, string> = {
  THB: '฿', USD: '$', EUR: '€', GBP: '£', JPY: '¥', KRW: '₩', CNY: '¥',
  VND: '₫', LAK: '₭', PHP: '₱', INR: '₹', IDR: 'Rp',
};

export function decimalsFor(currency = 'THB'): number {
  return EXPONENT[currency.toUpperCase()] ?? 2;
}

/**
 * The glyph an operator recognises for a currency, or the ISO code plus a
 * trailing space when we ship no glyph for it — the same fallback
 * {@link moneyHint} has always used, so a sentence that names the currency and
 * an amount rendered beside it cannot disagree.
 *
 * Exists so copy that has to SAY the currency (a help line, a column caption)
 * takes it from here rather than typing a baht sign, which is wrong the day a
 * non-THB brand opens the page (CLAUDE.md 2.7).
 */
export function currencySymbol(currency = 'THB'): string {
  return SYMBOLS[currency.toUpperCase()] ?? `${currency.toUpperCase()} `;
}

/**
 * Parse a user-entered major-unit decimal string (e.g. "100.50") into integer
 * minor units (10050) for the given currency. String-based — no float math, no
 * `Math.round`. Excess precision is truncated toward zero (§2.5 inbound, never
 * inflate). Returns 0 for blank/garbage input; callers gate required fields.
 */
export function toMinor(major: string | number, currency = 'THB'): number {
  return toMinorWithDecimals(major, decimalsFor(currency));
}

/**
 * The same parse as {@link toMinor}, answered as a DIGIT STRING carried through
 * `BigInt` instead of a JS `number`.
 *
 * `toMinor` finishes on `Number(...)`, which is exact only below 2^53. An
 * approval ceiling is the one field on this console where that edge is reachable
 * by an operator typing an ordinary-looking figure: in a zero-decimal currency
 * the minor units ARE the amount, so ten quadrillion rupiah is a number the box
 * accepts and `Number` rounds. The wire contract is a string for that reason,
 * and this is the parse that can honour it.
 *
 * Use it wherever the parsed value is sent onward rather than compared for
 * display; `toMinor` stays for the many boxes whose amounts cannot approach the
 * limit and whose callers want arithmetic.
 */
export function toMinorDigits(major: string | number, currency = 'THB'): string {
  const d = decimalsFor(currency);
  const text = String(major).trim();
  if (!text) return '0';
  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const [intRaw = '', fracRaw = ''] = body.split('.');
  const intDigits = intRaw.replace(/[^0-9]/g, '');
  const fracDigits = fracRaw.replace(/[^0-9]/g, '');
  // Truncated toward zero, never rounded up (§2.5 inbound).
  const frac = (fracDigits + '0'.repeat(d)).slice(0, d);
  const minor = BigInt(`${intDigits || '0'}${frac}`);
  if (minor === 0n) return '0';
  return negative ? `-${minor.toString()}` : minor.toString();
}

/**
 * Integer minor units typed literally, with NO exponent applied — the inverse
 * of {@link fromMinorUnits}.
 *
 * The platform-scope finance-config row has no brand behind it and therefore no
 * currency, so there is no exponent to divide or multiply by: reading a
 * 2-decimal one into it is the bug this exists to stop, and inventing any other
 * one is the same bug wearing a different number (#426). The operator sees and
 * types the stored integer itself.
 */
export function toMinorUnits(minor: string | number): number {
  return toMinorWithDecimals(minor, 0);
}

/** Render integer minor units literally — no exponent, no symbol, no grouping. */
export function fromMinorUnits(value: string | number | undefined | null): string {
  return fromMinorWithDecimals(value, 0);
}

function toMinorWithDecimals(major: string | number, d: number): number {
  const text = String(major).trim();
  if (!text) return 0;
  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const [intRaw = '', fracRaw = ''] = body.split('.');
  const intDigits = intRaw.replace(/[^0-9]/g, '');
  const fracDigits = fracRaw.replace(/[^0-9]/g, '');
  const frac = (fracDigits + '0'.repeat(d)).slice(0, d);
  const minor = Number(`${intDigits || '0'}${frac}`);
  if (!Number.isFinite(minor) || minor === 0) return 0;
  return negative ? -minor : minor;
}

/**
 * Inverse of {@link toMinor}: integer minor units → major-unit decimal string
 * (e.g. 10050 → "100.50", JPY 1000 → "1000"). Empty string for nullish input.
 */
export function fromMinor(value: string | number | undefined | null, currency = 'THB'): string {
  return fromMinorWithDecimals(value, decimalsFor(currency));
}

function fromMinorWithDecimals(value: string | number | undefined | null, d: number): string {
  if (value === undefined || value === null || value === '') return '';
  // The SIGN survives a string input. `value.replace(/[^0-9]/g, '')` stripped it,
  // so a negative amount that arrived as a decimal string — which is how
  // svc-commission serialises signed minor units — rendered POSITIVE: a node
  // that owed money was shown as having earned it. A number input kept its sign
  // through `Math.trunc`, so the two input shapes disagreed (W9).
  const text = typeof value === 'number' ? Math.trunc(value).toString() : String(value).trim();
  const negative = text.startsWith('-');
  const raw = (negative ? text.slice(1) : text).replace(/[^0-9]/g, '');
  if (!raw) return '';
  const sign = negative && /[1-9]/.test(raw) ? '-' : '';
  if (d === 0) return `${sign}${raw}`;
  const padded = raw.padStart(d + 1, '0');
  const head = padded.slice(0, padded.length - d);
  const tail = padded.slice(padded.length - d);
  return `${sign}${head}.${tail}`;
}

/**
 * Insert thousands separators into a MAJOR-unit decimal string, the shape
 * {@link fromMinor} returns: "1234.50" → "1,234.50", "-1234567" → "-1,234,567".
 *
 * String in, string out — the minor-unit value is never turned into a `number`,
 * so this neither loses precision past 2^53 nor assumes 2 decimal places. The
 * fraction is passed through untouched, which is what keeps a 0dp JPY amount
 * integral and a 3dp KWD amount at three places. Feed it `fromMinor(v, currency)`,
 * never a raw minor-unit string.
 */
export function groupMajor(major: string): string {
  if (!major) return major;
  const negative = major.startsWith('-');
  const [intPart = '', fracPart] = (negative ? major.slice(1) : major).split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${fracPart ? `.${fracPart}` : ''}`;
}

/** Format minor units with a currency symbol hint, e.g. "฿1,234.50" / "¥1,000". */
export function moneyHint(value: string | number | undefined, currency = 'THB'): string {
  const major = fromMinor(value, currency);
  if (!major) return '';
  const sym = currencySymbol(currency);
  // The sign is lifted out before grouping so it lands OUTSIDE the symbol
  // ("-฿1,234.50", not "฿-1,234.50").
  const negative = major.startsWith('-');
  return `${negative ? '-' : ''}${sym}${groupMajor(negative ? major.slice(1) : major)}`;
}

/**
 * Minor units → a money string with NO float anywhere on the path, for the
 * figures where the difference is reachable.
 *
 * `formatMoney` in `components/data-table/columns-helper` finishes on
 * `Number(minor) / 10 ** decimals`, which is the shape §2.2 forbids and is
 * exact only below 2^53. That is fine for a player's balance and is not fine
 * for an approval ceiling: on a 0-decimal brand the minor units ARE the amount,
 * so a limit an operator can type in the box next door already passes the edge,
 * and the wire carries these as decimal STRINGS for exactly that reason. A
 * ceiling that renders one unit off is the number a supervisor reconciles a
 * refusal against.
 *
 * The division is the digit-shifting one in {@link fromMinor} rather than
 * `BigInt(minor) / multiplier`: the two agree by construction — a decimal
 * exponent IS a shift of the digit string — and this one already carries the
 * sign rules and the per-currency precision, tested here and pinned against
 * `packages/shared/src/money.ts`. A second division would be a second place for
 * the exponent table to be applied slightly differently.
 *
 * `-` rather than a guess when the currency is unknown, and `-` rather than an
 * empty cell when the amount is: the same contract `formatMoneyIn` answers on,
 * so a call site swaps between them without changing what a blank row says.
 */
export function formatMinorExact(
  minor: string | number | null | undefined,
  currency: string | null | undefined,
): string {
  if (!currency) return '-';
  if (minor === null || minor === undefined || minor === '') return '-';
  const rendered = moneyHint(minor, currency);
  return rendered === '' ? '-' : rendered;
}
